import React from 'react';
import * as API from '../../api/api';
import * as Storage from '../../common/Storage';
import * as Util from '../../common/Util';
import { DOGIT_V3_EVENT_TYPE, DOGIT_V3_PROGRAM_TYPE } from '../../common/Constants';
import ChannelService from '../../common/ChannelService';
import LandingView from './view/LandingView';
import { UserInfoContext } from '../../components/PageWrapper';
import moment from 'moment';
import ImgView from '../program-detail/view/sub-view/ImgView';
import VideoView from '../program-detail/view/sub-view/VideoView';
import TotalReviewView from '../program-detail/view/sub-view/TotalReviewView';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import ReviewViewAnimation from '../program-detail/animation/ReviewViewAnimation';

export default function LandingV3Container() {
  const { userStatus, dgUserNo, hpNo } = React.useContext(UserInfoContext);
  const [isOpenMenu, setIsOpenMenu] = React.useState(false);
  const [isOpenVideoView, setIsOpenVideoView] = React.useState(false);
  const [isOpenImgView, setIsOpenImgView] = React.useState(false);
  const [isOpenReviewView, setIsOpenReviewView] = React.useState(false);
  const [reviewInfo, setReviewInfo] = React.useState({});
  const [offsetY, setOffsetY] = React.useState(0);

  const [reviewList, setReviewList] = React.useState([]);
  const [totalParticipatedCnt, setTotalParticipatedCnt] = React.useState(0);
  const [avgImprovementRate, setAvgImprovementRate] = React.useState(0);
  const [reviewCnt, setReviewCnt] = React.useState(0);
  const [isStartReviewScoreAnimation, setIsStartReviewScoreAnimation] = React.useState(false);

  React.useEffect(() => {
    API.getReviewInfo().then((res) => {
      setReviewList(res.data.reviewList);
      setAvgImprovementRate(res.data.avgImprovementRate);
      setTotalParticipatedCnt(res.data.totalParticipatedCnt);
      setReviewCnt(res.data.reviewCnt);
    });

    window.onload = () => {
      gsap.registerPlugin(ScrollTrigger);
      ReviewViewAnimation(setIsStartReviewScoreAnimation);
    };
  }, []);

  React.useEffect(() => {
    if (offsetY !== 0 && !isOpenImgView && !isOpenVideoView && !isOpenReviewView) {
      document.body.style.position = '';
      window.scrollTo(0, offsetY);
    }
  }, [isOpenImgView, isOpenVideoView, isOpenReviewView]);

  React.useEffect(() => {
    ChannelService.boot({
      pluginKey: '2809822e-6edb-481d-91a9-76a55c95a057',
      customLauncherSelector: '#dogit-channel-io-button',
      hideChannelButtonOnBoot: true,
    });
    return () => {
      ChannelService.shutdown();
    };
  }, []);

  React.useEffect(() => {
    Util.createEventLog(dgUserNo, hpNo, DOGIT_V3_EVENT_TYPE.VISITING);
  }, []);

  const onClickCloseMenu = () => {
    setIsOpenMenu(false);
  };

  const onClickOpenMenu = () => {
    Util.createEventLog(dgUserNo, hpNo, DOGIT_V3_EVENT_TYPE.MENU_BTN);
    setIsOpenMenu(true);
  };

  const onClickGotoDetail = (type) => {
    const [eventType, ga] =
      type === DOGIT_V3_PROGRAM_TYPE.SOCIALIZATION
        ? [DOGIT_V3_EVENT_TYPE.SOCIALIZATION_DETAIL_BTN, '사회화_상세_페이지_버튼']
        : type === DOGIT_V3_PROGRAM_TYPE.SEPARATION_ANXIETY
        ? [DOGIT_V3_EVENT_TYPE.SEPARATION_ANXIETY_DETAIL_BTN, '분리불안_상세_페이지_버튼']
        : type === DOGIT_V3_PROGRAM_TYPE.TOILET_TRAINING
        ? [DOGIT_V3_EVENT_TYPE.TOILET_TRAINING_DETAIL_BTN, '배변_상세_페이지_버튼']
        : type === DOGIT_V3_PROGRAM_TYPE.WALKING_TRAINING
        ? [DOGIT_V3_EVENT_TYPE.WALKING_TRAINING_DETAIL_BTN, '산책_상세_페이지_버튼']
        : type === DOGIT_V3_PROGRAM_TYPE.BARK
        ? [DOGIT_V3_EVENT_TYPE.BARK_DETAIL_BTN, '짖음_상세_페이지_버튼']
        : type === DOGIT_V3_PROGRAM_TYPE.ALL_IN_ONE
        ? [DOGIT_V3_EVENT_TYPE.ALL_IN_ONE_DETAIL_BYN, '올인원_상세_페이지_버튼']
        : type === DOGIT_V3_PROGRAM_TYPE.CUSTOMER_SERVICE_PROGRAM
        ? [DOGIT_V3_EVENT_TYPE.CUSTOMER_SERVICE_DETAIL_BTN, '전화상담_상세_페이지_버튼']
        : type === DOGIT_V3_PROGRAM_TYPE.VST_TRAINING
        ? [DOGIT_V3_EVENT_TYPE.VST_TRAINING_BTN, '방문훈련_상세_페이지_버튼']
        : ['', ''];

    Util.createEventLog(dgUserNo, hpNo, eventType, `/v3-program-detail?type=${type}`);
  };
  const onClickGotoHome = () => {
    const path =
      window.location.pathname.indexOf('/') === 0
        ? '랜딩'
        : window.location.pathname.indexOf('/v3-program-detail') === 0
        ? '상세'
        : '';
    Util.createEventLog(dgUserNo, hpNo, DOGIT_V3_EVENT_TYPE.INDEX_BTN, '/');
  };

  const onClickOpenVideoView = (src, review) => {
    setOffsetY(window.scrollY);
    document.body.style.position = 'fixed';
    setReviewInfo({
      src,
      review: review.review,
      starRate: review.starRate,
      userName: getUserName(review.dgUserProgramInfo.dgUserInfo.userName),
      petKind: review.dgUserProgramInfo.dgPetInfo.petKind,
      age: `${moment().diff(review.dgUserProgramInfo.dgPetInfo.petBirthYm, 'years')}세`,
      missionCompleteCnt: review.missionCompleteCnt,
      badge: [
        review.dgUserProgramInfo.dgProgramInfo.programName.includes('짖음') ? '🙀 짖음 개선' : '😿 분리불안 개선',
        review.improvement === 1
          ? '🥰 개선 되었어요'
          : review.improvement === 2
          ? '😉 어느정도 개선'
          : '🙅🏻‍♀️ 개선되지 않았어요',
      ],
    });
    setIsOpenVideoView(true);
  };

  const onClickOpenImgView = (src, review) => {
    setOffsetY(window.scrollY);
    document.body.style.position = 'fixed';
    setReviewInfo({
      src,
      review: review.review,
      starRate: review.starRate,
      userName: getUserName(review.dgUserProgramInfo.dgUserInfo.userName),
      petKind: review.dgUserProgramInfo.dgPetInfo.petKind,
      age: `${moment().diff(review.dgUserProgramInfo.dgPetInfo.petBirthYm, 'years')}세`,
      missionCompleteCnt: review.missionCompleteCnt,
      badge: [
        review.dgUserProgramInfo.dgProgramInfo.programName.includes('짖음') ? '🙀 짖음 개선' : '😿 분리불안 개선',
        review.improvement === 1
          ? '🥰 개선 되었어요'
          : review.improvement === 2
          ? '😉 어느정도 개선'
          : '🙅🏻‍♀️ 개선되지 않았어요',
      ],
    });
    setIsOpenImgView(true);
  };

  const onClickOpenReviewView = () => {
    setOffsetY(window.scrollY);
    document.body.style.position = 'fixed';
    setIsOpenReviewView(true);
  };

  const onClickCloseVideoView = () => {
    setIsOpenVideoView(false);
    setReviewInfo({});
  };

  const onClickCloseImgView = () => {
    setIsOpenImgView(false);
    setReviewInfo({});
  };

  const onClickCloseReviewView = () => {
    setIsOpenReviewView(false);
  };
  const getUserName = (userName) => {
    if (userName.length === 2) {
      return `${userName[0]}*`;
    } else if (userName.length === 3) {
      return `${userName[0]}*${userName[1]}`;
    } else {
      return `${userName[0]}**${userName[userName.length - 1]}`;
    }
  };

  return (
    <>
      {isOpenImgView && <ImgView reviewInfo={reviewInfo} onClickCloseImgView={onClickCloseImgView} />}
      {isOpenVideoView && <VideoView reviewInfo={reviewInfo} onClickCloseVideoView={onClickCloseVideoView} />}
      {isOpenReviewView && <TotalReviewView onClickCloseReviewView={onClickCloseReviewView} />}
      <LandingView
        userStatus={userStatus}
        isOpenMenu={isOpenMenu}
        onClickOpenMenu={onClickOpenMenu}
        onClickCloseMenu={onClickCloseMenu}
        onClickGotoHome={onClickGotoHome}
        onClickGotoDetail={onClickGotoDetail}
        onClickOpenVideoView={onClickOpenVideoView}
        onClickOpenImgView={onClickOpenImgView}
        onClickOpenReviewView={onClickOpenReviewView}
        getUserName={getUserName}
        reviewList={reviewList}
        totalParticipatedCnt={totalParticipatedCnt}
        avgImprovementRate={avgImprovementRate}
        reviewCnt={reviewCnt}
        isStartReviewScoreAnimation={isStartReviewScoreAnimation}
      />
    </>
  );
}
